import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Typography, Button, Space, Alert, DatePicker, Radio, Divider, Input } from 'antd';
import type { Dayjs } from 'dayjs';
import { useUser } from '../../contexts/UserContext';
import type { BillingProduct } from '../../services/api/billingService';
import { createCheckoutSession } from '../../services/api/billingService';
import { getApiErrorMessage } from '../../utils/apiErrors';
import { resolveWorkspaceCompanyId } from '../../utils/resolveWorkspaceCompanyId';
import { ONBOARDING_BUNDLE_CATALOG } from './onboardingBundleCatalog';

const { Title, Paragraph } = Typography;

/**
 * Conversion gate: catalog bundle selection; Stripe Checkout + manual scheduling fallback.
 */
export const OnboardingPaywallPage: React.FC = () => {
  const navigate = useNavigate();
  const {
    user,
    selectedCompany,
    availableCompanies: companiesList = [],
    loadUserCompanies,
    isAuthenticated,
  } = useUser();
  const companyId = useMemo(
    () => resolveWorkspaceCompanyId(user?.companyId, selectedCompany?.id, companiesList),
    [user?.companyId, selectedCompany?.id, companiesList]
  );

  useEffect(() => {
    if (!user?.id || !isAuthenticated) return;
    void loadUserCompanies();
  }, [user?.id, isAuthenticated, loadUserCompanies]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<BillingProduct>('foundation_session');
  /** Optional preferred session time (sent to Stripe metadata / webhook) */
  const [preferredSessionAt, setPreferredSessionAt] = useState<Dayjs | null>(null);
  /** Optional Stripe Promotion Code (customer-facing) */
  const [promotionCode, setPromotionCode] = useState('');

  const selectedEntry = useMemo(
    () => ONBOARDING_BUNDLE_CATALOG.find((e) => e.product === selectedProduct),
    [selectedProduct]
  );

  const startStripeCheckout = async () => {
    if (!companyId) {
      setError(
        'Payment is tied to a workspace company. Select your company in Control Bridge (or complete signup with a company), then return here.'
      );
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const trimmedPromo = promotionCode.trim();
      const { checkout_url } = await createCheckoutSession({
        product: selectedProduct,
        company_id: companyId,
        return_origin: window.location.origin,
        ...(preferredSessionAt ? { scheduled_at: preferredSessionAt.toISOString() } : {}),
        ...(trimmedPromo ? { promotion_code: trimmedPromo } : {}),
      });
      window.location.assign(checkout_url);
    } catch (e: unknown) {
      setError(getApiErrorMessage(e, 'Could not start checkout. Try again or use manual scheduling below.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: 720, margin: '40px auto', padding: 24 }}>
      <Card>
        <Title level={3} style={{ marginTop: 0 }}>
          Krysalis catalog checkout
        </Title>
        <Paragraph>
          Choose a product below, then continue to secure Stripe Checkout. After your foundation model is built, you
          receive a period of full platform access; continued access may be billed monthly (subscription bundles).
        </Paragraph>
        {!companyId && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message="Company required"
            description="We could not determine which workspace company to bill. If you have several companies, open Control Bridge and select one, then return here."
          />
        )}
        {error && (
          <Alert type="error" showIcon style={{ marginBottom: 16 }} message={error} />
        )}

        <Divider orientation="left">Select product</Divider>
        <Radio.Group
          value={selectedProduct}
          onChange={(e) => setSelectedProduct(e.target.value as BillingProduct)}
          style={{ width: '100%' }}
          aria-label="Select catalog product for checkout"
        >
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            {ONBOARDING_BUNDLE_CATALOG.map((entry) => (
              <Card
                key={entry.product}
                size="small"
                style={{
                  cursor: 'pointer',
                  borderColor: selectedProduct === entry.product ? '#1677ff' : undefined,
                }}
                onClick={() => setSelectedProduct(entry.product)}
              >
                <Radio value={entry.product}>{entry.title}</Radio>
                <Paragraph type="secondary" style={{ marginBottom: 0, marginLeft: 24 }}>
                  <Typography.Text strong>{entry.priceLabel}</Typography.Text>
                  {' — '}
                  {entry.description}
                </Paragraph>
                <Typography.Text type="secondary" style={{ marginLeft: 24, fontSize: 12 }}>
                  {entry.kind === 'subscription' ? 'Subscription (monthly)' : 'One-time checkout'}
                </Typography.Text>
              </Card>
            ))}
          </Space>
        </Radio.Group>

        {selectedEntry?.kind === 'session_or_package' && (
          <div style={{ marginTop: 16, marginBottom: 8 }}>
            <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
              Preferred session time (optional — recorded in checkout metadata)
            </Typography.Text>
            <DatePicker
              showTime={{ format: 'HH:mm' }}
              format="YYYY-MM-DD HH:mm"
              style={{ width: '100%', maxWidth: 320 }}
              value={preferredSessionAt}
              onChange={(v: Dayjs | null) => setPreferredSessionAt(v)}
              aria-label="Preferred session date and time"
            />
          </div>
        )}

        <div style={{ marginTop: 16, marginBottom: 8 }}>
          <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
            Promotion code (optional)
          </Typography.Text>
          <Input
            allowClear
            placeholder="Enter code if you have one"
            value={promotionCode}
            onChange={(e) => setPromotionCode(e.target.value)}
            style={{ maxWidth: 320 }}
            aria-label="Stripe promotion code"
            autoComplete="off"
          />
        </div>

        <Paragraph type="secondary" style={{ fontSize: 13, marginTop: 16 }}>
          Exact calendar booking may still be coordinated with the team; after payment, Stripe confirms your purchase
          and the platform records the checkout metadata.
        </Paragraph>

        <Space wrap style={{ marginTop: 24 }}>
          <Button type="primary" size="large" loading={loading} onClick={() => void startStripeCheckout()}>
            Pay with card (secure checkout)
          </Button>
          <Button size="large" onClick={() => navigate('/onboarding/confirmation')}>
            Mark Foundation Session as scheduled
          </Button>
          <Button size="large" onClick={() => navigate('/onboarding/prospector')}>
            Back to discovery chat
          </Button>
          <Button type="link" onClick={() => navigate('/dashboard')}>
            Go to Control Bridge
          </Button>
        </Space>
      </Card>
    </div>
  );
};
